"use client";

import { EmptyView } from "../empty-view";
import ChartAxisUnit from "./ChartAxisUnit";
import AreaChart from "./area-chart";
import BarChart from "./bar-chart";
import LineChart from "./line-chart";
import MixedChart from "./mixed-chart";
import { getTranslation, Lang } from "@/data/translation";
import { convertInstanceDataToChartData } from "@/helpers/chart/convert-instance-data-to-chart-data";
import useFetchData from "@/hook/use-fetch-data";
import { usePrevious } from "@/hook/use-previous";
import { PortfolioReturnsPeriod } from "@/services/custom-portfolio/custom-portfolio-returns";
import {
  Chart13FInstanceParams,
  ChartComponentParams,
  ChartType,
  CustomPortfolioInstanceParams,
  InstanceDataConfig,
} from "@/types/chart";
import { ChartData, ChartOptions } from "chart.js";
import { useEffect, useState } from "react";

type ChartComponentProps = {
  lang: Lang;
  options: ChartOptions<"bar" | "line">;
  className?: string;
  chart13FParams?: Chart13FInstanceParams;
  customPortfolioParams?: CustomPortfolioInstanceParams;
  portfolioReturnsPeriod?: PortfolioReturnsPeriod;
} & Pick<
  ChartComponentParams,
  | "title"
  | "chartKey"
  | "chartType"
  | "instanceData"
  | "instanceDataConfig"
  | "instanceLabels"
  | "timeFrame"
  | "range"
  | "symbols"
  | "customColorset"
  | "chartRenderErrorCallback"
>;

const ChartComponent = ({
  lang,
  title,
  chartKey,
  chartType,
  className,
  options,
  chart13FParams,
  customPortfolioParams,
  customColorset,
  portfolioReturnsPeriod,
  symbols,
  range,
  timeFrame,
  instanceData,
  instanceLabels,
  instanceDataConfig,
  chartRenderErrorCallback,
}: ChartComponentProps) => {
  const { t } = getTranslation({ lang });
  const { data, error, loading } = useFetchData({
    lang,
    chartKey,
    chartType,
    symbol: symbols?.join(","),
    range,
    timeFrame,
    chart13FParams,
    customPortfolioParams,
    portfolioReturnsPeriod,
    enabled: !instanceData,
  });
  const previousLoading = usePrevious(loading);

  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [isInitial, setIsInitial] = useState(false);
  const isMixedChartType =
    (instanceDataConfig || []).reduce((acc, { chartType }) => {
      acc.add(chartType);
      return acc;
    }, new Set<ChartType>()).size > 1;

  useEffect(() => {
    // update chart data when fetch data is successful
    if (data) {
      setChartData(data);
      setIsInitial(true);
    }
  }, [data]);

  useEffect(() => {
    if (instanceData) {
      const chartData = convertInstanceDataToChartData({
        chartType,
        instanceData,
        instanceLabels,
        timeFrame,
        range,
        instanceDataConfig,
        customColorset,
      });
      setChartData(chartData);
      setIsInitial(true);
    }
  }, [instanceData, instanceLabels, chartType, timeFrame, range]);

  const emptyCheckData = data || chartData;

  const isEmpty =
    !emptyCheckData ||
    emptyCheckData?.datasets.length === 0 ||
    emptyCheckData?.labels?.length === 0;

  useEffect(() => {
    if (loading === previousLoading || loading) return;

    chartRenderErrorCallback?.({
      isError: !!error,
      isEmpty: !emptyCheckData,
    });
  }, [error, isEmpty, loading]);

  if (!isInitial) {
    return null;
  }

  if (error || isEmpty)
    return <ErrorState title={title} message={t.error.noData} />;

  if (isMixedChartType) {
    return (
      <ChartWrapper
        className={className}
        instanceDataConfig={instanceDataConfig}
      >
        <MixedChart data={chartData as ChartData<"line">} options={options} />
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper className={className} instanceDataConfig={instanceDataConfig}>
      {chartType === "bar" && (
        <BarChart data={chartData as ChartData<"bar">} options={options} />
      )}
      {chartType === "line" && (
        <LineChart data={chartData as ChartData<"line">} options={options} />
      )}
      {chartType === "area" && (
        <AreaChart data={chartData as ChartData<"line">} options={options} />
      )}
    </ChartWrapper>
  );
};

const ErrorState = ({
  title,
  message,
}: {
  title?: string;
  message: string;
}) => (
  <ChartWrapper>
    {title && <ErrorViewTitle title={title} />}
    <EmptyView>{message}</EmptyView>
  </ChartWrapper>
);

const ErrorViewTitle = ({ title }: { title: string }) => (
  <p className="text-xs font-semibold text-[#666666]">{title}</p>
);

const ChartWrapper = ({
  className,
  children,
  instanceDataConfig,
}: {
  className?: string;
  children: React.ReactNode;
  instanceDataConfig?: InstanceDataConfig[];
}) => (
  <div
    className={`relative w-full h-full flex justify-center items-center flex-col gap-5 rounded-lg bg-zinc-100 p-2 ${className}`}
  >
    <ChartAxisUnit instanceDataConfig={instanceDataConfig} />
    {children}
  </div>
);

export default ChartComponent;
